import { useCore } from '@phoenix/core/frontend/shared/feature/context/core.context';
import { useRefreshLinks } from '@phoenix/core/frontend/shared/feature/global-links/use-refresh-links';
import { getLocale } from '@phoenix/core/frontend/shared/util/locales/locales';
import { useQueryContextMemo } from '@phoenix/team-m/dashboard/frontend/shared/use-query-context-memo';
import {
  Product,
  MaintenanceDocumentFile,
  useGetUserProductsQuery,
  Maybe,
} from '@phoenix/team-m/dashboard/frontend/domain/generated/graphql';
import { mapToProductCard, sortByServiceReminder } from '@phoenix/team-m/dashboard/frontend/util/product/product.mapper';

export const useUserProducts = ({ withServiceReminder = false, withManual = false } = {}) => {
  const {
    auth: { user },
  } = useCore();

  const stihlId = user?.stihl_id || '';

  const [{ data, fetching, error }] = useGetUserProductsQuery({
    variables: { stihlId, limit: 25, withServiceReminder, withManual },
    pause: stihlId === '',
    context: useQueryContextMemo(process.env.NEXT_PUBLIC_TOOL_SHED_GRAPHQL_BASE_URL),
  });

  const { searchmanuals } = useRefreshLinks([{ id: 'searchmanuals' }], getLocale(), withManual);
  const getManual = (file?: Maybe<MaintenanceDocumentFile>): string | undefined =>
    (file?.type === 'PDF' && file.url) || (searchmanuals?.url !== '#' && searchmanuals?.url) || undefined;

  const userProducts = (data?.user?.products?.items as Array<Product>)?.map((p) => ({
    ...mapToProductCard(p),
    manualUrl: getManual(p.productManualFile),
  }));

  const getProductsCategories = () =>
    userProducts?.reduce(
      (prev, curr) =>
        curr.productCategory?.name && !prev.includes(curr.productCategory.name) ? [...prev, curr.productCategory.name] : prev,
      [] as string[]
    );

  return {
    userProducts,
    sortedByServiceReminder: () => sortByServiceReminder(userProducts ?? []),
    userProductsCategories: getProductsCategories(),
    isLoading: (fetching || !data) && !error,
  };
};
